import React from "react";
import { toast } from 'react-toastify';
import { useDealerContext } from "./DealerContext";
import useApi from "../../lib/apiService.js";

const InventoryContext = React.createContext()

const NONE = { status: "NONE", vehicles: [] };
const LOADING = { status: "LOADING", vehicles: [] };
const ERROR = { status: "ERROR", vehicles: [] };

function InventoryContextProvider({ children }) {
    const Api = useApi();
    const context = useDealerContext();
    const [vehiclesVa, setVehiclesVa] = React.useState(NONE);
    const [vehiclesCvi, setVehiclesCvi] = React.useState(NONE);
    const [vehiclesCvi2, setVehiclesCvi2] = React.useState(NONE);
    const [vehiclesMvs, setVehiclesMvs] = React.useState(NONE);
    const [vehiclesIol, setVehiclesIol] = React.useState(NONE);
    const [vehiclesDdc, setVehiclesDdc] = React.useState(NONE);
    const [vehiclesVin, setVehiclesVin] = React.useState(NONE);
    const [vehiclesAtc, setVehiclesAtc] = React.useState(NONE);
    const [vehiclesVaSource, setVehiclesVaSource] = React.useState(NONE);

    React.useEffect(() => {
        (async () => {
            if (context.state.vaDealerId) {
                setVehiclesVa(LOADING);
                try {
                    let vehicles = await Api.getVautoVehiclesForDealer(context.state.vaDealerId, context.state.includeSold);
                    setVehiclesVa({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting vAuto vehicles:", e);
                    toast.error("Problem getting vAuto vehicles");
                    setVehiclesVa(ERROR);
                }
            } else {
                setVehiclesVa(NONE);
            }
        })();
    }, [context.state.vaDealerId, context.state.includeSold]);

    React.useEffect(() => {
        (async () => {
            if (context.state.entityId) {
                setVehiclesCvi(LOADING);
                try {
                    let vehicles = await Api.getCviVehiclesForEntity(context.state.entityId, context.state.includeSold);
                    setVehiclesCvi({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting CVI vehicles:", e);
                    toast.error("Problem getting CVI vehicles");
                    setVehiclesCvi(ERROR);
                }
            } else {
                setVehiclesCvi(NONE);
            }
        })();
    }, [context.state.entityId, context.state.includeSold]);

    // CVI 2.1 is a separate data source from CVI 1.0 — the same vehicle can exist in both, so it
    // is loaded and labeled independently rather than replacing the 1.0 list.
    React.useEffect(() => {
        (async () => {
            if (context.state.cvi2DealerId) {
                setVehiclesCvi2(LOADING);
                try {
                    let vehicles = await Api.getCvi2VehiclesForDealer(context.state.cvi2DealerId);
                    setVehiclesCvi2({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting CVI 2.1 vehicles:", e);
                    toast.error("Problem getting CVI 2.1 vehicles");
                    setVehiclesCvi2(ERROR);
                }
            } else {
                setVehiclesCvi2(NONE);
            }
        })();
    }, [context.state.cvi2DealerId]);

    React.useEffect(() => {
        (async () => {
            if (context.state.entityId) {
                setVehiclesMvs(LOADING);
                try {
                    let vehicles = await Api.getMvsVehiclesForEntity(context.state.entityId);
                    setVehiclesMvs({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting MVS vehicles:", e);
                    toast.error("Problem getting MVS vehicles");
                    setVehiclesMvs(ERROR);
                }
            } else {
                setVehiclesMvs(NONE);
            }
        })();
    }, [context.state.entityId]);

    React.useEffect(() => {
        (async () => {
            if (context.state.entityId && context.state.includeInternal) {
                setVehiclesVaSource(LOADING);
                try {
                    let vehicles = await Api.getVautoSourceVehiclesForEntity(context.state.entityId);
                    setVehiclesVaSource({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting vA source vehicles:", e);
                    toast.error("Problem getting vA source vehicles");
                    setVehiclesVaSource(ERROR);
                }
            } else {
                setVehiclesVaSource(NONE);
            }
        })();
    }, [context.state.entityId, context.state.includeInternal]);

    React.useEffect(() => {
        (async () => {
            if (context.state.iolVrtId) {
                setVehiclesIol(LOADING);
                try {
                    let vehicles = await Api.getIolVehiclesForVrt(context.state.iolVrtId);
                    setVehiclesIol({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting IOL vehicles:", e);
                    toast.error("Problem getting IOL vehicles");
                    setVehiclesIol(ERROR);
                }
            } else {
                setVehiclesIol(NONE);
            }
        })();
    }, [context.state.iolVrtId]);

    React.useEffect(() => {
        (async () => {
            if (context.state.ddcDealerId) {
                setVehiclesDdc(LOADING);
                try {
                    let vehicles = await Api.getDdcVehiclesForDealer(context.state.ddcDealerId);
                    setVehiclesDdc({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting DDC vehicles:", e);
                    toast.error("Problem getting DDC vehicles");
                    setVehiclesDdc(ERROR);
                }
            } else {
                setVehiclesDdc(NONE);
            }
        })();
    }, [context.state.ddcDealerId]);

    React.useEffect(() => {
        (async () => {
            if (context.state.vinDealerId) {
                setVehiclesVin(LOADING);
                try {
                    let vehicles = await Api.getVinVehiclesForDealer(context.state.vinDealerId);
                    setVehiclesVin({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting VIN vehicles:", e);
                    toast.error("Problem getting VIN vehicles");
                    setVehiclesVin(ERROR);
                }
            } else {
                setVehiclesVin(NONE);
            }
        })();
    }, [context.state.vinDealerId]);

    React.useEffect(() => {
        (async () => {
            if (context.state.atcDealerId) {
                setVehiclesAtc(LOADING);
                try {
                    let vehicles = await Api.getAtcVehiclesForDealer(context.state.atcDealerId);
                    setVehiclesAtc({ status: "READY", vehicles: vehicles });
                } catch (e) {
                    console.error("Problem getting ATC vehicles:", e);
                    toast.error("Problem getting ATC vehicles");
                    setVehiclesAtc(ERROR);
                }
            } else {
                setVehiclesAtc(NONE);
            }
        })();
    }, [context.state.atcDealerId]);

    const allSets = [vehiclesVa, vehiclesCvi, vehiclesCvi2, vehiclesMvs, vehiclesIol, vehiclesDdc, vehiclesVin, vehiclesAtc, vehiclesVaSource];
    const isReady = () => {
        if (allSets.some((s) => s.status === "LOADING")) return false;
        if (allSets.some((s) => s.status === "READY" || s.status === "ERROR")) return true;
        return false;
    }
    const isLoading = () => {
        if (allSets.some((s) => s.status === "LOADING")) return true;
        if (allSets.some((s) => s.status === "READY" || s.status == "ERROR")) return true;
        return false;
    }

    // NOTE: you *might* need to memoize this value
    // Learn more in http://kcd.im/optimize-context
    const value = {
        cviSvsInventory: vehiclesCvi,
        cvi2Inventory: vehiclesCvi2,
        cviVaSrcInventory: vehiclesVaSource,
        vaCoreInventory: vehiclesVa,
        vaMvsInventory: vehiclesMvs,
        iolInventory: vehiclesIol,
        ddcInventory: vehiclesDdc,
        vinInventory: vehiclesVin,
        atcInventory: vehiclesAtc,
        isReady: isReady(),
        isLoading: isLoading(),
    }
    return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>
}

function useInventoryContext() {
    const context = React.useContext(InventoryContext)
    if (context === undefined) {
        throw new Error('useInventoryContext must be used within an InventoryProvider');
    }
    return context;
}

export { InventoryContextProvider, useInventoryContext };